import {
  addCreditCardFee,
  bookingCancelWithPayment,
  bookingCancelWithPaymentPaymentSuccess,
  bookingGetBookingSuccess,
  bookingPaymentsDeclined,
  checkCreditCardFee,
  checkCreditCardFeeSucceed,
  loadCreditCardFees,
  loadCreditCardFeesSucceed,
  resetCreditCardFee, resetCreditCardFeeSucceed,
} from '@actions/booking.action';
import {
  clearPaymentErrors,
  clearPaymentFlow,
  PaymentActionTypes,
  paymentApplyCreditFile,
  paymentApplyCreditFileSuccess,
  paymentApplyVoucher,
  paymentApplyVoucherSuccess,
  paymentCreditFileVerificationFailure,
  paymentCreditFileVerificationSuccess,
  paymentRequestVCCData,
  paymentSubmitPayments,
  paymentSubmitPaymentsSuccess,
  paymentVerifyCreditFile,
  paymentVerifyVoucher,
  paymentVoucherVerificationSuccess, setPaymentInProgress,
} from '@actions/payment.action';
import { Injectable } from '@angular/core';
import { BottomImageModalComponent } from '@components/modals/bottom-image-modal/bottom-image-modal.component';
import {
  CultureKeyType,
  CULTURE_ROUTE_PARAM,
  MODAL_DIALOG_CENTER_LG,
} from '@constants/app.constants';
import { BaseError, ErrorType } from '@models/app.models';
import { PaymentCopy } from '@models/cms/payment.model';
import { ExternalPaymentRequest } from '@models/requests/external-payment-request.model';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import {
  selectBookingBreakdown,
  selectBookingHasPendingCreditCardPayments,
  selectBookingHasReachedMaxConsecutiveDeclinedPayments,
  selectBookingPrimaryContact,
  selectBookingCurrencyCode,
  selectEarliestDepartureDate,
  selectEarliestJourney,
} from '@selectors/booking.selector';
import { selectPaymentCopy } from '@selectors/cms.selector';
import {
  selectCreditFileInformation, selectIsCorporateCreditCard,
  selectPaymentFlow,
  selectVoucherVerificationInformation,
} from '@selectors/payment.selector';
import { selectRouteParam, selectUrl } from '@selectors/router.selector';
import { selectLoggedInUserCustomerNumber } from '@selectors/user.selector';
import { PaymentService } from '@services/api-clients/payment.service';
import { ModalService } from '@services/modal/modal.service';
import { getRootPath } from '@utils/data-utils';
import { Observable, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { appAbandonToken, appNoOp, onError } from '../actions/app.action';
import { UpliftService } from '@services/uplift/uplift.service';
import { PaymentFlow } from '@models/payment.model';
import { selectPhoneCodes } from '@selectors/resource.selector';
import {OrchestrationService} from "@services/api-clients/orchestration.service";
import {CreditCardFeeMap, FeeType} from "../../../typings/credit-card-type";
import {CorporateCreditCardFeeResponse} from "@models/responses/payment-api-response.model";
import {GrowthBookService} from "@services/growthbook/growthbook.service";

@Injectable()
export class PaymentEffect {
  submitPayments$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(paymentSubmitPayments, bookingCancelWithPayment),
      concatLatestFrom(() => [
        this.store.select(selectBookingCurrencyCode),
        this.store.select(selectRouteParam(CULTURE_ROUTE_PARAM)) as Observable<
          CultureKeyType | undefined
        >,
        this.store.select(selectUrl),
        this.store.select(selectVoucherVerificationInformation),
        this.store.select(selectBookingHasPendingCreditCardPayments),
        this.store.select(selectPaymentCopy),
        this.store.select(selectPaymentFlow),
        this.store.select(selectPhoneCodes),
      ]),
      mergeMap(
        ([
          action,
          currency,
          cultureCode,
          url,
          ,
          bookingHasPendingCreditCardPayments,
          copy,
          paymentFlow,
          phoneCodes
        ]) => {
          if (bookingHasPendingCreditCardPayments) {
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
            const paymentCopy = copy as PaymentCopy | null;
            this.modalService.show(BottomImageModalComponent, {
              class: MODAL_DIALOG_CENTER_LG,
              initialState: {
                btnText: paymentCopy?.okayCopy,
                text: paymentCopy?.pendingPaymentsPleaseRetryCopy,
                img: 'https://assets.flynorse.com/static/site/modals/three-tails.jpg',
                altText: paymentCopy?.norsePlanesCopy,
              },
            });
            return of(
              onError({
                error: new BaseError(ErrorType.SubmitPaymentFailure),
              }),
            );
          }

          const flow = getRootPath(url);
          let paymentRequests: ExternalPaymentRequest[] | null = null;
          try {
            paymentRequests = action.payments.map((payment) =>
              this.paymentService.transformFormDataToSubmitPaymentRequest(
                currency,
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                cultureCode!,
                payment.amount,
                payment,
                flow,
                paymentFlow,
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                phoneCodes!
              ),
            );
          } catch (ex) {
            return of(
              onError({
                error: new BaseError(ErrorType.SubmitPaymentFailure),
              }),
            );
          }

          return this.paymentService.submitPayments(paymentRequests).pipe(
            map(() =>
              action.type === PaymentActionTypes.submitPayments
                ? paymentSubmitPaymentsSuccess()
                : bookingCancelWithPaymentPaymentSuccess(),
            ),
            catchError(() =>
              of(
                onError({
                  error: new BaseError(ErrorType.SubmitPaymentFailure),
                }),
              ),
            ),
          );
        },
      ),
    );
  });

  checkForMaxDeclinedPayments$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(bookingGetBookingSuccess),
      concatLatestFrom(() => [
        this.store.select(
          selectBookingHasReachedMaxConsecutiveDeclinedPayments,
        ),
        this.store.select(selectPaymentCopy),
      ]),
      switchMap(([, hasReachedMaxDeclinedPayments, copy]) => {
        if (!hasReachedMaxDeclinedPayments) {
          return of(appNoOp());
        }

        const modal = this.modalService.show(BottomImageModalComponent, {
          class: MODAL_DIALOG_CENTER_LG,
          initialState: {
            btnText: copy?.okayCopy,
            text: copy?.tooManyFailedPaymentsCopy,
            img: 'https://assets.flynorse.com/static/site/modals/three-tails.jpg',
            altText: copy?.norsePlanesCopy,
          },
        });

        if (!modal.onHidden) {
          return of(appNoOp());
        }

        return modal.onHidden.pipe(
          switchMap(() => {
            return of(appAbandonToken());
          }),
        );
      }),
    );
  });

  verifyVoucher$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(paymentVerifyVoucher),
      switchMap(({ voucherCode }) => {
        return this.paymentService.verifyVoucher(voucherCode).pipe(
          map(({ data: voucherInformation }) => {
            return paymentVoucherVerificationSuccess({
              voucherInformation,
            });
          }),
        );
      }),
    );
  });

  applyVoucher$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(paymentApplyVoucher),
      concatLatestFrom(() => [
        this.store.select(selectBookingCurrencyCode),
        this.store.select(selectVoucherVerificationInformation),
        this.store.select(selectBookingBreakdown),
      ]),
      mergeMap(([action, currency, voucherVerificationStatuses, breakdown]) => {
        const balanceDue = breakdown?.balanceDue;
        const redeemableAmount =
          voucherVerificationStatuses[action.voucherCode]?.redeemableAmount;

        if (!redeemableAmount) {
          return of(
            onError({
              error: new BaseError(
                ErrorType.ApplyVoucherFailure,
                'Attempting to apply unverified voucher',
              ),
            }),
          );
        }

        const voucherRequest =
          this.paymentService.transformFormDataToSubmitVoucherRequest(
            currency,
            balanceDue
              ? Math.min(balanceDue, redeemableAmount)
              : redeemableAmount,
            action.voucherCode,
          );

        return this.paymentService.submitVoucher(voucherRequest).pipe(
          map(() =>
            paymentApplyVoucherSuccess({
              voucherCode: action.voucherCode,
            }),
          ),
          catchError(() =>
            of(
              onError({
                error: new BaseError(ErrorType.ApplyVoucherFailure),
              }),
            ),
          ),
        );
      }),
    );
  });

  verifyCreditFile$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(paymentVerifyCreditFile),
      concatLatestFrom(() => [
        this.store.select(selectBookingPrimaryContact),
        this.store.select(selectBookingCurrencyCode),
      ]),
      switchMap(([action, primaryContact, currency]) => {
        if (!primaryContact) {
          return of(
            paymentCreditFileVerificationFailure({
              creditFilePnr: action.creditFilePnr,
            }),
          );
        }

        return this.paymentService
          .verifyCreditFile(
            action.creditFilePnr,
            primaryContact.name.last,
            currency,
          )
          .pipe(
            map(({ data: creditFileInformation }) => {
              return paymentCreditFileVerificationSuccess({
                creditFilePnr: action.creditFilePnr,
                creditFileInformation,
              });
            }),
          );
      }),
    );
  });

  applyCreditFile$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(paymentApplyCreditFile),
      concatLatestFrom(() => [
        this.store.select(selectBookingCurrencyCode),
        this.store.select(selectCreditFileInformation),
        this.store.select(selectBookingBreakdown),
        this.store.select(selectEarliestDepartureDate),
        this.store.select(selectBookingPrimaryContact),
        this.store.select(selectEarliestJourney),
        this.store.select(selectLoggedInUserCustomerNumber),
      ]),
      mergeMap(
        ([
          { creditFilePnr },
          currency,
          creditFileVerificationInformation,
          breakdown,
          earliestDepartureDate,
          primaryContact,
          earliestJourney,
          customerNumber,
        ]) => {
          const balanceDue = breakdown?.balanceDue;
          const creditFileAmount =
            creditFileVerificationInformation[creditFilePnr]?.amount;

          if (!creditFileAmount) {
            return of(
              onError({
                error: new BaseError(ErrorType.ApplyCreditFileFailure),
              }),
            );
          }

          const creditFileRequest =
            this.paymentService.transformFormDataToSubmitCreditFileRequest(
              currency,
              balanceDue
                ? Math.min(balanceDue, creditFileAmount)
                : creditFileAmount,
              creditFilePnr,
              primaryContact?.name.first,
              primaryContact?.name.last,
              primaryContact?.emailAddress,
              earliestDepartureDate,
              earliestJourney?.designator.origin,
              customerNumber,
            );

          return this.paymentService.submitCreditFile(creditFileRequest).pipe(
            map(() =>
              paymentApplyCreditFileSuccess({
                creditFilePnr,
              }),
            ),
            catchError(() =>
              of(
                onError({
                  error: new BaseError(ErrorType.ApplyCreditFileFailure),
                }),
              ),
            ),
          );
        },
      ),
    );
  });

  paymentRequestVCCData$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(paymentRequestVCCData),
        tap(() => {
            this.upliftService.requestVCCData();
          },
        ),
      );
    },
    {
      dispatch: false,
    },
  );

  paymentSubmitError$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(onError),
        concatLatestFrom(() => [
          this.store.select(selectPaymentFlow),
        ]),
        tap(
          ([{
            error: { type },
          }, flow]) => {
            if (flow === PaymentFlow.UPLIFT_LOAN && [ErrorType.SubmitPaymentFailure, ErrorType.BookingCompleteFailure, ErrorType.BookingCommitFailure].some(e => e === type)) {
              this.upliftService.error(ErrorType[type], 'fatal')
            }
          },
        ),
      );
    },
    {
      dispatch: false,
    },
  );

  paymentDeclined$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(bookingPaymentsDeclined),
        concatLatestFrom(() => [
          this.store.select(selectPaymentFlow),
        ]),
        tap(
          ([{
            error: { type },
          }, flow]) => {
            if (flow === PaymentFlow.UPLIFT_LOAN && ErrorType.BookingPaymentsDeclined === type) {
              this.upliftService.error(ErrorType[type], 'booking')
            }
          },
        ),
      );
    },
    {
      dispatch: false,
    },
  );

  clearPaymentFlow$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(clearPaymentFlow),
      map(() => [
        setPaymentInProgress({ paymentInProgress: false }),
        clearPaymentErrors()
      ]),
      mergeMap(actions => actions)
    ) }
  );

  loadCreditCardFeesEffect: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(loadCreditCardFees),
      switchMap(() => {
        return this.orchestrationService.loadCreditCardFees().pipe(
          map((response: { fees: CreditCardFeeMap }) =>
            loadCreditCardFeesSucceed({ fees: response.fees })
          ),
        );
      })
    );
  });

  checkCreditCardFeeEffect: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(checkCreditCardFee),
      switchMap((action) => {
        return this.orchestrationService.checkCreditCardFee(false, false, action.cardBin).pipe(
          map((response: CorporateCreditCardFeeResponse) => {
            if (response.feeType === FeeType.CORPORATE) {
              if (this.gbs.isVariantOfExperimentEnabled('feat-002430-corporate-credit-card-fee', 'b')) {
                return addCreditCardFee({cardBin: action.cardBin})
              }
            }
              return checkCreditCardFeeSucceed({response})
            }
          ),
        );
      })
    );
  });

  addCreditCardFeeEffect: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(addCreditCardFee),
      switchMap((action) => {
        return this.orchestrationService.checkCreditCardFee(false, true, action.cardBin).pipe(
          map((response: CorporateCreditCardFeeResponse) => {
              return checkCreditCardFeeSucceed({response})
            }
          ),
        );
      })
    );
  });

  resetCreditCardFeeEffect: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(resetCreditCardFee),
      switchMap(() => {
        return this.orchestrationService.checkCreditCardFee(true, false).pipe(
          map(() => {
            return resetCreditCardFeeSucceed()
          }),
        );
      })
    );
  });


  constructor(
    private actions$: Actions,
    private paymentService: PaymentService,
    private orchestrationService: OrchestrationService,
    private store: Store,
    private modalService: ModalService,
    private upliftService: UpliftService,
    private gbs: GrowthBookService,
  ) {}
}
